import React, { memo } from "react";

import { formatCurrency } from "helpers";
import { formatUnitPrice } from "services/api/pricing/consts";
import { Loader } from "ui/components/Loader";

import { SubscriptionEstimatePanelProps } from "../../types";

const formatPercent = (value: number): string =>
  `${value.toFixed(2).replace(".", ",").replace(",00", "")} %`;

const formatTierRange = (fromVehicle: number, toVehicle: number | null): string =>
  toVehicle === null ? `${fromVehicle}+` : `${fromVehicle}–${toVehicle}`;

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString("hr-HR");

/**
 * Procjena mjesečnog iznosa.
 *
 * Nijedan broj ovdje nije izračunat u pregledniku — svi dolaze s backenda, kroz isti resolver
 * i ista pravila koja vrijede pri potpisu.
 */
const SubscriptionEstimatePanel: React.FC<SubscriptionEstimatePanelProps> = (props) => {
  const { estimate, meteredNames, isLoading } = props;

  if (isLoading) {
    return (
      <div className="border border-slate-200 px-4 py-8">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  if (!estimate) {
    return null;
  }

  if (!estimate.amounts) {
    return (
      <div className="border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm text-amber-900 font-medium">Iznos nije izračunat.</p>

        {estimate.errors.map((error) => (
          <p key={error} className="text-sm text-amber-800 mt-1">
            {error}
          </p>
        ))}
      </div>
    );
  }

  const amounts = estimate.amounts;
  const isProRated = amounts.chargedDays !== amounts.daysInMonth;

  return (
    <div className="border border-slate-200">
      {/*---------------------------------------------------------------
          PRETPLATA
        ---------------------------------------------------------------*/}
      <div className="px-4 py-4 border-b border-slate-200">
        <div className="flex items-baseline justify-between">
          <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
            Pretplata
          </h3>
          <span className="text-2xl font-bold text-slate-900">
            {formatCurrency.format(amounts.fullMonth)}
          </span>
        </div>

        {estimate.priceTiers.length > 0 && (
          <div className="mt-3 space-y-1 text-xs">
            {estimate.priceTiers.map((tier) => (
              <div
                key={tier.fromVehicle}
                className="flex justify-between text-slate-500"
              >
                <span>
                  {formatTierRange(tier.fromVehicle, tier.toVehicle)} vozila ·{" "}
                  {tier.vehiclesInTier} × {formatUnitPrice.format(estimate.unitPrice)}
                  {tier.ratePercent !== 100 && ` × ${formatPercent(tier.ratePercent)}`}
                </span>
                <span>{formatCurrency.format(tier.amount)}</span>
              </div>
            ))}

            <div className="flex justify-between text-slate-700 font-medium pt-1 border-t border-slate-100">
              <span>Po vozilima</span>
              <span>{formatCurrency.format(amounts.byVehicles)}</span>
            </div>
          </div>
        )}

        {amounts.isMinimumApplied && (
          <p className="text-xs text-amber-700 mt-2">
            Iznos po vozilima ({formatCurrency.format(amounts.byVehicles)}) je ispod minimuma,
            pa se naplaćuje minimalna naknada od {formatCurrency.format(amounts.minimum)}.
          </p>
        )}
      </div>

      {/*---------------------------------------------------------------
          POTROŠNJA
        ---------------------------------------------------------------*/}
      <div className="px-4 py-4 border-b border-slate-200">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
            Procijenjena potrošnja
          </h3>
          <span className="text-sm font-medium text-slate-900">
            {formatCurrency.format(amounts.meteredTotal)}
          </span>
        </div>

        <div className="space-y-1 text-xs">
          {estimate.metered.map((item) => (
            <div key={item.keyCode} className="flex justify-between text-slate-500">
              <span>
                {meteredNames[item.keyCode] ?? item.keyCode} · {item.quantity} ×{" "}
                {formatUnitPrice.format(item.unitPrice)}
              </span>
              <span>{formatCurrency.format(item.amount)}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-400 mt-2">
          Potrošnja se naplaćuje kako nastane, po stvarnim količinama.
        </p>
      </div>

      {/*---------------------------------------------------------------
          UKUPNO
        ---------------------------------------------------------------*/}
      <div className="px-4 py-4 bg-slate-50">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-slate-900">Mjesečno ukupno</span>
          <span className="text-2xl font-bold text-slate-900">
            {formatCurrency.format(amounts.monthlyTotal)}
          </span>
        </div>

        {isProRated && (
          <div className="flex justify-between text-xs text-slate-600 mt-2 pt-2 border-t border-slate-200">
            <span>
              Prvi obračun od {formatDate(amounts.firstChargedOn)} ({amounts.chargedDays} od{" "}
              {amounts.daysInMonth} dana)
            </span>
            <span className="font-medium">
              {formatCurrency.format(amounts.firstMonth)} + potrošnja
            </span>
          </div>
        )}

        {amounts.annualTotal !== null && (
          <div className="flex justify-between text-xs text-slate-600 mt-2 pt-2 border-t border-slate-200">
            <span>
              Godišnje, uz popust od {formatPercent(estimate.annualDiscountPercent)}
            </span>
            <span className="font-medium">
              {formatCurrency.format(amounts.annualTotal)} + potrošnja
            </span>
          </div>
        )}

        {estimate.trialEndsOn && (
          <p className="text-xs text-emerald-700 mt-2 pt-2 border-t border-slate-200">
            Probni period traje {estimate.trialDays} dana — ne naplaćuje se do{" "}
            {formatDate(estimate.trialEndsOn)}.
          </p>
        )}
      </div>
    </div>
  );
};

export default memo(SubscriptionEstimatePanel);
